import { ZoneInfo } from './../administration/administration/administration-details/zone-details/zone-details.component';
import { Component, OnInit, Renderer2 } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { QrCodeType } from 'src/app/base/qr-code-type';
import { QueryParameter } from 'src/app/base/query-parameters';
import { MobileLandingService } from './mobile-landing.service';
import { Browser } from '@syncfusion/ej2-base';
import { environment } from 'src/environments/environment';
import { ParkingSessionControlType, ZoneInfoDTO } from 'src/app/base/models/models.swagger.generated';
import { fastCheckinRoute, payTillNowRoute } from 'src/app/core/routing/route-names';
import { TranslateModule } from '@ngx-translate/core';
import { MobileHeaderComponent } from './shared/mobile-header/mobile-header.component';
import { DialogModule } from '@syncfusion/ej2-angular-popups';
import { DropDownListModule } from '@syncfusion/ej2-angular-dropdowns';

interface ZoneSelectionItem {
  zoneId: number;
  zoneName: string;
}

@Component({
  selector: 'app-mobile-landing',
  imports: [TranslateModule, MobileHeaderComponent, DialogModule, DropDownListModule],
  templateUrl: './mobile-landing.component.html',
  styleUrl: './mobile-landing.component.scss',
})
export class MobileLandingComponent implements OnInit {
  public errorMessage: string = 'i18n.domain.mobile-landing.errors.zoneNotFound';
  public showError: boolean = false;
  public showZoneSelectionDialog: boolean = false;
  public zoneSelectionItems: ZoneSelectionItem[] = [];
  public selectedZoneId?: number;
  public zoneDropdownFields = { text: 'zoneName', value: 'zoneId' };

  private zoneInfos: ZoneInfoDTO[] = [];

  constructor(
    private renderer: Renderer2,
    private route: ActivatedRoute,
    private router: Router,
    private service: MobileLandingService
  ) {}

  ngOnInit() {
    this.route.queryParams.subscribe(params => {
      const type = params[QueryParameter.QrCodeType];
      if (type === QrCodeType.Zone.toString()) {
        const zoneId = params[QueryParameter.QrCodeId];
        this.service.fetchParkingProcessZoneInfo(zoneId).subscribe({next: zoneInfo => {
          if (zoneInfo?.sessionControlType == ParkingSessionControlType.HardwareControlled) {
            this.router.navigate([payTillNowRoute], { queryParams: { [QueryParameter.ZoneId]: zoneId } });
          } else if (zoneInfo?.fastCheckinSubscriptionId > 0) {
            this.router.navigate([fastCheckinRoute], { queryParams: { [QueryParameter.ZoneId]: zoneId } });
          } else {
            this.errorMessage = 'i18n.domain.mobile-landing.errors.zoneNotFound';
            this.showError = true;

            // redirect to store page if possible
            this.redirectToAppStoreIfPossible();
          }
        },
        error: err => {
          this.errorMessage = 'i18n.domain.mobile-landing.errors.zoneNotFound';
          this.showError = true;
        }});
      } else if (type === QrCodeType.Visitor.toString()) {
        const validationCode = params[QueryParameter.QrCodeId];
        this.service.loadZoneInfosForValidationCode(validationCode).subscribe({next: zoneInfos => {
          this.zoneInfos = zoneInfos?.zoneInfos?.filter(zoneInfo => !!zoneInfo?.zone?.id) ?? [];
          this.handleZoneInfos(this.zoneInfos);
        },
        error: err => {
          this.errorMessage = 'i18n.domain.mobile-landing.errors.zoneNotFound';
          this.showError = true;
        }});
      }
    });

    this.renderer.addClass(document.body, 'body-login');
  }

  private redirectToAppStoreIfPossible() {
    if (Browser.isAndroid) {
      setTimeout(() => {
        window.location.href = environment.playStoreUrl;
      }, 3000);
    } else if (Browser.isIos) {
      setTimeout(() => {
        window.location.href = environment.appStoreUrl;
      }, 3000);
    }
  }

  public onZoneDialogClosed() {
    this.showZoneSelectionDialog = false;
  }

  public onCancelZoneSelection() {
    this.showZoneSelectionDialog = false;
    this.zoneSelectionItems = [];
    this.selectedZoneId = undefined;
    this.errorMessage = 'i18n.domain.mobile-landing.errors.processAborted';
    this.showError = true;
    // redirect to store page if possible
    this.redirectToAppStoreIfPossible();
  }

  public onConfirmZoneSelection() {
    if (!this.selectedZoneId) {
      return;
    }

    this.showZoneSelectionDialog = false;
    this.zoneSelectionItems = [];
    const zoneId = this.selectedZoneId;
    this.selectedZoneId = undefined;

    const zoneInfo = this.zoneInfos.find(z => z.zone?.id === zoneId);
    if (zoneInfo?.zone.sessionControlType == ParkingSessionControlType.HardwareControlled) {
      this.router.navigate([payTillNowRoute], { queryParams: { [QueryParameter.ZoneId]: zoneId, [QueryParameter.ParkerId]: zoneInfo?.validatingParkerId } });
      return;
    }

    this.router.navigate([fastCheckinRoute], { queryParams: { [QueryParameter.ZoneId]: zoneId } });
  }

  private handleZoneInfos(zoneInfos: ZoneInfoDTO[]) {
    const validZoneInfos = zoneInfos.filter(zoneInfo => !!zoneInfo?.zone?.id);

    if (validZoneInfos.length === 1) {
      this.selectedZoneId = validZoneInfos[0].zone?.id;
      this.onConfirmZoneSelection();
      return;
    }

    if (validZoneInfos.length > 1) {
      this.zoneSelectionItems = validZoneInfos.map(zoneInfo => ({
        zoneId: zoneInfo.zone!.id,
        zoneName: zoneInfo.zone?.name || 'Unnamed zone',
      }));
      this.selectedZoneId = undefined;
      this.showZoneSelectionDialog = true;
      this.showError = false;
      return;
    }

    this.errorMessage = 'i18n.domain.mobile-landing.errors.zoneNotFound';
    this.showError = true;
  }

}
